import { Box, Button, Flex, Link } from '@chakra-ui/react'
import ArrowRightOmens from 'components/svg/ArrowRightOmens'
import { Body1 } from 'components/typography'
import { FC, useEffect, useState } from 'react'
import { ARROW_NUDGE_ANIMATION } from 'styles/animations'

import { HOME_CTA_HREF, HOME_CTA_LABEL, HOME_HERO_ID } from '../constants'

/**
 * Mobile sticky CTA of the current landing (`MedleveStickyCTA`), isolated: appears once the hero is
 * half scrolled away, same destination and `a > button` shape as the hero CTA.
 * It is mounted on the client only, so the server HTML carries no invisible duplicate of the CTA
 * (PRD §76); the breakpoint is handled with CSS instead of a JS media query.
 */
const HomeStickyCta: FC = () => {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setMounted(true)
    const heroEl = document.getElementById(HOME_HERO_ID)
    if (!heroEl || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      // Show the sticky CTA when the hero is NOT intersecting (user scrolled past it). The negative top
      // rootMargin makes that happen while ~50% of the hero is still visible.
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-50% 0px 0px 0px' }
    )
    observer.observe(heroEl)
    return () => observer.disconnect()
  }, [])

  if (!mounted) return null

  return (
    <Box
      display={{ base: 'block', lg: 'none' }}
      position="fixed"
      left="10px"
      right="10px"
      bottom="max(10px, env(safe-area-inset-bottom))"
      zIndex={50}
      pointerEvents={visible ? 'auto' : 'none'}
      visibility={visible ? 'visible' : 'hidden'}
      opacity={visible ? 0.95 : 0}
      transform={visible ? 'translateY(0)' : 'translateY(20px)'}
      transition="opacity 0.2s ease, transform 0.2s ease, visibility 0.2s"
    >
      <Link href={HOME_CTA_HREF} _hover={{ textDecoration: 'none' }}>
        <Button
          w="full"
          h="auto"
          px="24px"
          py="22px"
          borderRadius="24px"
          bg="#4774B8"
          color="alabaster"
          boxShadow="0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.2)"
          _hover={{ bg: '#5A8AD4' }}
        >
          <Flex w="full" justify="space-between" align="center">
            <Body1
              fontWeight={800}
              fontSize="19px"
              sx={{ fontFeatureSettings: "'ss01' on, 'liga' off, 'calt' off" }}
              letterSpacing="-0.01em"
              lineHeight="1.4rem"
            >
              {HOME_CTA_LABEL}
            </Body1>
            <ArrowRightOmens
              fill="alabaster"
              boxSize="20px"
              aria-hidden="true"
              sx={{ animation: ARROW_NUDGE_ANIMATION }}
            />
          </Flex>
        </Button>
      </Link>
    </Box>
  )
}

export default HomeStickyCta
